import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { buildAbsoluteUrl, siteSeo, truncateSeoText } from "../seo/seoConfig";

export default function SEO({
  title,
  description,
  path,
  image,
  type = "website",
  robots = "index,follow",
  keywords = [],
  canonical,
  structuredData,
}) {
  const location = useLocation();

  const seo = useMemo(() => {
    const rawTitle = title || siteSeo.defaultTitle;
    const fullTitle = rawTitle.includes(siteSeo.siteName)
      ? rawTitle
      : siteSeo.titleTemplate.replace("%s", rawTitle);
    const cleanDescription = truncateSeoText(description || siteSeo.defaultDescription);
    const canonicalUrl = canonical || buildAbsoluteUrl(path || location.pathname || "/");
    const imageUrl = buildAbsoluteUrl(image || siteSeo.defaultImage);

    return {
      title: fullTitle,
      description: cleanDescription,
      canonicalUrl,
      imageUrl,
      robots,
      keywords: Array.isArray(keywords) ? keywords.filter(Boolean).join(", ") : keywords,
      structuredData,
    };
  }, [canonical, description, image, keywords, location.pathname, path, robots, structuredData, title]);

  return (
    <Helmet htmlAttributes={{ lang: siteSeo.language }}>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="robots" content={seo.robots} />
      <meta name="author" content={siteSeo.author} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      {import.meta.env.VITE_GOOGLE_SITE_VERIFICATION && (
        <meta name="google-site-verification" content={import.meta.env.VITE_GOOGLE_SITE_VERIFICATION} />
      )}

      <link rel="canonical" href={seo.canonicalUrl} />

      <meta property="og:site_name" content={siteSeo.siteName} />
      <meta property="og:locale" content={siteSeo.locale} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.canonicalUrl} />
      <meta property="og:image" content={seo.imageUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.imageUrl} />
      {siteSeo.twitterHandle && <meta name="twitter:site" content={siteSeo.twitterHandle} />}

      {seo.structuredData && (
        <script type="application/ld+json">{JSON.stringify(seo.structuredData)}</script>
      )}
    </Helmet>
  );
}

export function AnalyticsTags() {
  useEffect(() => {
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    const gtmId = import.meta.env.VITE_GTM_ID;

    if (gaId && !document.getElementById("ga-loader")) {
      const gaScript = document.createElement("script");
      gaScript.id = "ga-loader";
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
      document.head.appendChild(gaScript);

      const gaInit = document.createElement("script");
      gaInit.id = "ga-init";
      gaInit.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId}');
      `;
      document.head.appendChild(gaInit);
    }

    if (gtmId && !document.getElementById("gtm-loader")) {
      const gtmScript = document.createElement("script");
      gtmScript.id = "gtm-loader";
      gtmScript.textContent = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');
      `;
      document.head.appendChild(gtmScript);
    }
  }, []);

  return null;
}
